import { create } from 'zustand';
import toast from 'react-hot-toast';

import { getDisplayError } from '@utils/get-display-error';
import { trackEvent } from '../../../utils/track-event';
import {
  resolveTrailerCheck,
  addTrailerCheckNote,
  updateTrailerCheckResolution,
  updateTrailerCheckNote,
  updateTrailerCheck,
  type TrailerCheckDetailResponse,
  type TrailerCheckEventTypeType,
  type TrailerCheckStatusType,
} from '../models';

export function eventDateToInputValue(isoDate: string): string {
  const d = new Date(isoDate);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export type DetailsDraft = {
  status: TrailerCheckStatusType;
  cannotContinue: boolean;
  generalRemarks: string;
  eventType: TrailerCheckEventTypeType;
  eventDate: string;
  locationName: string;
  isActive: boolean;
  rentalStartDate: string;
  rentalEndDate: string;
};

export type TrailerCheckDetailUiState = {
  resolution: {
    note: string;
    isResolving: boolean;
    editing: boolean;
    editedNote: string;
    isSaving: boolean;
  };
  notes: {
    newContent: string;
    isAdding: boolean;
    editingId: string | null;
    editedContent: string;
    isSaving: boolean;
  };
  details: {
    editing: boolean;
    draft: DetailsDraft;
    isSaving: boolean;
  };
};

function createInitialState(): TrailerCheckDetailUiState {
  return {
    resolution: {
      note: '',
      isResolving: false,
      editing: false,
      editedNote: '',
      isSaving: false,
    },
    notes: {
      newContent: '',
      isAdding: false,
      editingId: null,
      editedContent: '',
      isSaving: false,
    },
    details: {
      editing: false,
      draft: {
        status: 'OK',
        cannotContinue: false,
        generalRemarks: '',
        eventType: 'PickedUp',
        eventDate: '',
        locationName: '',
        isActive: true,
        rentalStartDate: '',
        rentalEndDate: '',
      },
      isSaving: false,
    },
  };
}

type MutateCheck = () => Promise<TrailerCheckDetailResponse | undefined>;

type TrailerCheckDetailStore = TrailerCheckDetailUiState & {
  reset: () => void;
  setResolutionNote: (note: string) => void;
  startResolutionEdit: (note: string) => void;
  cancelResolutionEdit: () => void;
  setEditedResolutionNote: (note: string) => void;
  setNotesNewContent: (content: string) => void;
  startNoteEdit: (noteId: string, content: string) => void;
  cancelNoteEdit: () => void;
  setEditedNoteContent: (content: string) => void;
  startDetailsEdit: (check: TrailerCheckDetailResponse) => void;
  cancelDetailsEdit: () => void;
  patchDetailsDraft: (patch: Partial<DetailsDraft>) => void;
  handleResolve: (checkId: string, mutate: MutateCheck) => Promise<void>;
  handleAddNote: (checkId: string, mutate: MutateCheck) => Promise<void>;
  handleSaveResolution: (checkId: string, mutate: MutateCheck) => Promise<void>;
  handleSaveNote: (checkId: string, noteId: string, mutate: MutateCheck) => Promise<void>;
  handleSaveDetails: (checkId: string, mutate: MutateCheck) => Promise<void>;
};

export const useTrailerCheckDetailStore = create<TrailerCheckDetailStore>((set, get) => ({
  ...createInitialState(),

  reset: () => set(createInitialState()),

  setResolutionNote: (note) => set((state) => ({ resolution: { ...state.resolution, note } })),

  startResolutionEdit: (note) =>
    set((state) => ({
      resolution: { ...state.resolution, editing: true, editedNote: note },
    })),

  cancelResolutionEdit: () =>
    set((state) => ({
      resolution: { ...state.resolution, editing: false, editedNote: '', isSaving: false },
    })),

  setEditedResolutionNote: (note) => set((state) => ({ resolution: { ...state.resolution, editedNote: note } })),

  setNotesNewContent: (content) => set((state) => ({ notes: { ...state.notes, newContent: content } })),

  startNoteEdit: (noteId, content) =>
    set((state) => ({
      notes: { ...state.notes, editingId: noteId, editedContent: content },
    })),

  cancelNoteEdit: () =>
    set((state) => ({
      notes: { ...state.notes, editingId: null, editedContent: '', isSaving: false },
    })),

  setEditedNoteContent: (content) => set((state) => ({ notes: { ...state.notes, editedContent: content } })),

  startDetailsEdit: (check) =>
    set({
      details: {
        editing: true,
        isSaving: false,
        draft: {
          status: check.status,
          cannotContinue: check.cannotContinue,
          generalRemarks: check.generalRemarks,
          eventType: check.eventType,
          eventDate: eventDateToInputValue(check.eventDate),
          locationName: check.locationName ?? '',
          isActive: check.isActive,
          rentalStartDate: check.rentalStartDate ? eventDateToInputValue(check.rentalStartDate) : '',
          rentalEndDate: check.rentalEndDate ? eventDateToInputValue(check.rentalEndDate) : '',
        },
      },
    }),

  cancelDetailsEdit: () =>
    set((state) => ({
      details: { ...state.details, editing: false, isSaving: false },
    })),

  patchDetailsDraft: (patch) =>
    set((state) => ({
      details: {
        ...state.details,
        draft: { ...state.details.draft, ...patch },
      },
    })),

  handleResolve: async (checkId, mutate) => {
    const { resolution } = get();
    if (!resolution.note.trim()) {
      toast.error('Voeg een opmerking toe');
      return;
    }

    set((state) => ({ resolution: { ...state.resolution, isResolving: true } }));
    try {
      await resolveTrailerCheck(checkId, { resolutionNote: resolution.note.trim() });
      trackEvent('trailer_check_resolved', { check_id: checkId });
      toast.success('Trailer check opgelost');
      set((state) => ({
        resolution: { ...state.resolution, note: '', isResolving: false },
      }));
      await mutate();
    } catch (err) {
      toast.error('Mislukt: ' + getDisplayError(err));
      set((state) => ({ resolution: { ...state.resolution, isResolving: false } }));
    }
  },

  handleAddNote: async (checkId, mutate) => {
    const { notes } = get();
    if (!notes.newContent.trim()) return;

    set((state) => ({ notes: { ...state.notes, isAdding: true } }));
    try {
      await addTrailerCheckNote(checkId, { content: notes.newContent.trim() });
      toast.success('Notitie toegevoegd');
      set((state) => ({
        notes: { ...state.notes, newContent: '', isAdding: false },
      }));
      await mutate();
    } catch (err) {
      toast.error('Mislukt: ' + getDisplayError(err));
      set((state) => ({ notes: { ...state.notes, isAdding: false } }));
    }
  },

  handleSaveResolution: async (checkId, mutate) => {
    const { resolution } = get();
    if (!resolution.editedNote.trim()) {
      toast.error('Voeg een opmerking toe');
      return;
    }

    set((state) => ({ resolution: { ...state.resolution, isSaving: true } }));
    try {
      await updateTrailerCheckResolution(checkId, { resolutionNote: resolution.editedNote.trim() });
      toast.success('Oplossing bijgewerkt');
      get().cancelResolutionEdit();
      await mutate();
    } catch (err) {
      toast.error('Mislukt: ' + getDisplayError(err));
    }
    set((state) => ({ resolution: { ...state.resolution, isSaving: false } }));
  },

  handleSaveNote: async (checkId, noteId, mutate) => {
    const { notes } = get();
    if (!notes.editedContent.trim()) return;

    set((state) => ({ notes: { ...state.notes, isSaving: true } }));
    try {
      await updateTrailerCheckNote(checkId, noteId, { content: notes.editedContent.trim() });
      toast.success('Notitie bijgewerkt');
      get().cancelNoteEdit();
      await mutate();
    } catch (err) {
      toast.error('Mislukt: ' + getDisplayError(err));
    }
    set((state) => ({ notes: { ...state.notes, isSaving: false } }));
  },

  handleSaveDetails: async (checkId, mutate) => {
    const { draft } = get().details;
    set((state) => ({ details: { ...state.details, isSaving: true } }));
    try {
      await updateTrailerCheck(checkId, {
        status: draft.status,
        cannotContinue: draft.cannotContinue,
        generalRemarks: draft.generalRemarks,
        eventType: draft.eventType,
        eventDate: new Date(draft.eventDate).toISOString(),
        locationName: draft.locationName || null,
        isActive: draft.isActive,
        rentalStartDate: draft.rentalStartDate || null,
        rentalEndDate: draft.rentalEndDate || null,
      });
      toast.success('Trailer check bijgewerkt');
      set((state) => ({
        details: { ...state.details, editing: false, isSaving: false },
      }));
      await mutate();
    } catch (err) {
      toast.error('Mislukt: ' + getDisplayError(err));
      set((state) => ({ details: { ...state.details, isSaving: false } }));
    }
  },
}));
